
import { logger } from "@/lib/services/logger-service"
import { supabase } from "@/integrations/supabase/client"
import { AIAnalysis } from "@/types/ai"
import { toast } from "@/components/ui/use-toast"

export class AIAdviceService {
  static async fetchRoomId(caregiverId: string): Promise<string | null> {
    try {
      // Get the authenticated user ID
      const { data: userData } = await supabase.auth.getUser()
      const authUserId = userData.user?.id
      
      if (!authUserId) {
        logger.error('Failed to get auth user ID for AI analysis')
        return null
      }
      
      // Get the specialist ID by querying the specialists table
      const { data: specialistData, error: specialistError } = await supabase
        .from('specialists')
        .select('id')
        .eq('auth_user_id', authUserId)
        .single()
      
      if (specialistError) {
        logger.error('Failed to get specialist ID', { specialistError })
        return null
      }
      
      const specialistId = specialistData.id
      
      // Query the chat_rooms table directly to find the room for this specialist and caregiver
      const { data: roomData, error: roomError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('specialist_id', specialistId)
        .eq('caregiver_id', caregiverId)
        .single()
      
      if (roomError) {
        logger.error('Failed to find chat room', { roomError, specialistId, caregiverId })
        throw roomError
      }
      
      if (!roomData) {
        logger.error('No chat room found for this specialist and caregiver', { 
          specialistId, 
          caregiverId 
        })
        return null
      }
      
      const roomIdFromData = roomData.id
      
      logger.debug('Successfully retrieved room ID for AI analysis', { 
        caregiverId,
        roomId: roomIdFromData
      })
      
      return roomIdFromData
    } catch (error) {
      logger.error('Error fetching room ID', { error, caregiverId })
      return null
    }
  }

  static async fetchAdvice(caregiverId: string, childId?: string): Promise<AIAnalysis | null> {
    try {
      logger.debug('Calling AI analysis edge function', { 
        caregiverId,
        childId
      })

      const { data, error } = await supabase.functions.invoke('generate-ai-analysis', {
        body: { caregiverId, childId }
      })

      if (error) {
        logger.error('Error from AI analysis edge function', { error })
        throw error
      }

      if (!data) {
        logger.error('No data returned from AI analysis')
        return null
      }

      logger.info('AI advice fetched successfully', { caregiverId, childId })
      return data as AIAnalysis
    } catch (error) {
      logger.error('Error fetching AI advice', { error, caregiverId, childId })
      return null
    }
  }
}
